import { PRODUCT_STATUS, STOCK_STATUS, ORDER_STATUS } from "./constants";

/**
 * Operational "needs attention" items for Marketplace Admin dashboard.
 */
export function buildMarketplaceNeedsAttentionItems({
  orders = [],
  products = [],
  awaitingConfirmation = [],
  lowStock = [],
  outOfStock = [],
  draftProducts = [],
  delayedProcessing = [],
  preOrderProducts = [],
}) {
  const items = [];
  const awaiting = awaitingConfirmation?.length ?? orders.filter((o) => o.status === ORDER_STATUS.AWAITING_CONFIRMATION).length;

  if (awaiting > 0) {
    items.push({
      id: "orders-awaiting-confirmation",
      count: awaiting,
      title: "Orders awaiting confirmation",
      description: "New marketplace orders need staff review and confirmation.",
      cta: "Review orders",
      path: "/super_admin/investor_portal/marketplace/orders",
      severity: "critical",
      badge: "Action needed",
    });
  }

  const low = lowStock?.length ?? products.filter((p) => p.stockStatus === STOCK_STATUS.LOW_STOCK).length;
  if (low > 0) {
    items.push({
      id: "low-stock",
      count: low,
      title: "Low-stock products",
      description: "Inventory below threshold — restock or adjust availability.",
      cta: "View products",
      path: "/super_admin/investor_portal/marketplace/products",
      severity: "warning",
      badge: "Low stock",
    });
  }

  const oos = outOfStock?.length ?? products.filter((p) => p.stockStatus === STOCK_STATUS.OUT_OF_STOCK).length;
  if (oos > 0) {
    items.push({
      id: "out-of-stock",
      count: oos,
      title: "Out-of-stock products",
      description: "Published or active products with zero available stock.",
      cta: "Manage inventory",
      path: "/super_admin/investor_portal/marketplace/products",
      severity: "critical",
      badge: "Out of stock",
    });
  }

  const drafts = draftProducts?.length ?? products.filter((p) =>
    [PRODUCT_STATUS.DRAFT, PRODUCT_STATUS.INTERNAL_REVIEW].includes(p.status)
  ).length;

  if (drafts > 0) {
    items.push({
      id: "draft-products",
      count: drafts,
      title: "Draft products not published",
      description: "Complete product content and publish to the marketplace.",
      cta: "View products",
      path: "/super_admin/investor_portal/marketplace/products",
      severity: "neutral",
      badge: "Draft",
    });
  }

  const delayed = delayedProcessing?.length ?? 0;
  if (delayed > 0) {
    items.push({
      id: "delayed-processing",
      count: delayed,
      title: "Orders delayed in processing",
      description: "Orders in processing for more than 3 days need follow-up.",
      cta: "View orders",
      path: "/super_admin/investor_portal/marketplace/orders",
      severity: "warning",
      badge: "Delayed",
    });
  }

  const preOrder = preOrderProducts?.length ?? products.filter((p) => p.stockStatus === STOCK_STATUS.PRE_ORDER).length;
  if (preOrder > 0) {
    items.push({
      id: "pre-order-followup",
      count: preOrder,
      title: "Pre-order products requiring follow-up",
      description: "Confirm supplier timelines and communicate ETA to buyers.",
      cta: "View products",
      path: "/super_admin/investor_portal/marketplace/products",
      severity: "info",
      badge: "Pre-order",
    });
  }

  return items.filter((i) => i.count > 0);
}

/** Hub-level marketplace attention from real dashboard summary (max 4 items). */
export function buildHubMarketplaceAttentionFromSummary(summary = {}) {
  const items = [];
  const awaiting = Number(summary.ordersAwaitingConfirmation || 0);
  const lowStock = Number(summary.lowStockProducts || 0);
  const outOfStock = Number(summary.outOfStockProducts || 0);

  if (awaiting > 0) {
    items.push({
      id: "hub-mp-orders",
      count: awaiting,
      title: "Marketplace orders awaiting confirmation",
      description: "Staff action required on new marketplace orders.",
      cta: "Review orders",
      path: "/super_admin/investor_portal/marketplace/orders",
      severity: "critical",
      badge: "Marketplace",
      group: "Marketplace",
    });
  }
  if (lowStock > 0) {
    items.push({
      id: "hub-mp-low-stock",
      count: lowStock,
      title: "Marketplace low-stock alerts",
      description: "Products below inventory threshold.",
      cta: "View products",
      path: "/super_admin/investor_portal/marketplace/products",
      severity: "warning",
      badge: "Inventory",
      group: "Marketplace",
    });
  }
  if (outOfStock > 0) {
    items.push({
      id: "hub-mp-oos",
      count: outOfStock,
      title: "Marketplace out-of-stock products",
      description: "Restock or unpublish unavailable products.",
      cta: "Manage products",
      path: "/super_admin/investor_portal/marketplace/products",
      severity: "warning",
      badge: "Out of stock",
      group: "Marketplace",
    });
  }

  return items;
}

/** @deprecated Prefer buildHubMarketplaceAttentionFromSummary with real API summary. */
export function buildHubMarketplaceAttention(companyId, getHubFn) {
  if (typeof getHubFn !== "function") return [];
  return buildHubMarketplaceAttentionFromSummary(getHubFn(companyId) || {});
}
